/**
 * AICartAnalyticsCard — weekly-window dashboard card for admins.
 *
 * Pulls a single aggregate from `GET /api/admin/ai-cart/analytics` and
 * surfaces three headline signals:
 *   1. Tier upgrades (Bronze / Silver / Gold count in the window)
 *   2. Credit burn rate (credits spent on fresh URL ingests)
 *   3. Top-performing marketplaces (top 5 by ingest count)
 *
 * A window selector lets the admin toggle 7 / 30 / 90 days. Refetches
 * on selector change; auto-refetches every 60 s while the tab is open.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, TrendingUp, Coins, Store, Award, Users, Zap } from "lucide-react";
import { api } from "@/lib/api";

interface Analytics {
  window_days: number;
  tier_upgrades: { bronze: number; silver: number; gold: number };
  tier_upgrades_total: number;
  credit_burn: number;
  credit_cost_per_search: number;
  top_marketplaces: { key: string; count: number }[];
  total_ingests: number;
  unique_users: number;
}

const WINDOW_OPTIONS = [7, 30, 90];

export const AICartAnalyticsCard: React.FC = () => {
  const [days, setDays] = React.useState(7);

  const q = useQuery<Analytics>({
    queryKey: ["admin-ai-cart-analytics", days],
    queryFn: async () => (await api.get(`/admin/ai-cart/analytics?days=${days}`)).data,
    refetchInterval: 60_000,
  });

  return (
    <div className="sg-card p-5 sm:p-6 space-y-5" data-testid="admin-ai-cart-analytics">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-lg grid place-items-center bg-gradient-to-br from-emerald-500 to-cyan-500 text-white shrink-0">
          <TrendingUp className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-lg text-slate-900 dark:text-white">
            AI Cart™ · Weekly analytics
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Tier climbers, credit burn and marketplace mix — for the selected window.
          </div>
        </div>
        <div
          className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-1 gap-0.5"
          data-testid="ai-cart-analytics-window"
        >
          {WINDOW_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`ai-cart-analytics-window-${d}`}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                days === d
                  ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {q.isLoading || !q.data ? (
        <div className="text-sm text-slate-500 py-6 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Crunching numbers…
        </div>
      ) : (
        <>
          {/* Headline tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile
              icon={<Award className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
              label="Tier upgrades"
              value={q.data.tier_upgrades_total}
              hint={`🥉 ${q.data.tier_upgrades.bronze} · 🥈 ${q.data.tier_upgrades.silver} · 🥇 ${q.data.tier_upgrades.gold}`}
              testId="ai-cart-analytics-tier-total"
            />
            <StatTile
              icon={<Coins className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
              label="Credits burned"
              value={q.data.credit_burn}
              hint={`${q.data.credit_cost_per_search} credit per fresh search`}
              testId="ai-cart-analytics-credit-burn"
            />
            <StatTile
              icon={<Zap className="h-4 w-4 text-fuchsia-600 dark:text-fuchsia-400" />}
              label="URL ingests"
              value={q.data.total_ingests}
              hint="Includes cache hits"
              testId="ai-cart-analytics-ingests"
            />
            <StatTile
              icon={<Users className="h-4 w-4 text-sky-600 dark:text-sky-400" />}
              label="Unique users"
              value={q.data.unique_users}
              hint="Distinct signed-in shoppers"
              testId="ai-cart-analytics-users"
            />
          </div>

          {/* Top marketplaces bar chart */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Store className="h-4 w-4 text-slate-500" />
              <div className="text-sm font-semibold text-slate-900 dark:text-white">
                Top marketplaces
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 ml-auto">
                by ingest count
              </div>
            </div>
            {q.data.top_marketplaces.length === 0 ? (
              <div className="text-xs text-slate-500 py-2">No ingests in this window yet.</div>
            ) : (
              <div className="space-y-1.5" data-testid="ai-cart-analytics-marketplaces">
                {q.data.top_marketplaces.map((mp) => {
                  const max = Math.max(...q.data!.top_marketplaces.map((m) => m.count)) || 1;
                  const pct = Math.round((mp.count / max) * 100);
                  return (
                    <div key={mp.key} className="grid grid-cols-[100px_1fr_48px] gap-2 items-center" data-testid={`ai-cart-analytics-marketplace-${mp.key}`}>
                      <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate capitalize">
                        {mp.key}
                      </div>
                      <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="text-xs tabular-nums text-right font-mono text-slate-600 dark:text-slate-400">
                        {mp.count}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const StatTile: React.FC<{
  icon: React.ReactNode; label: string; value: number; hint?: string; testId?: string;
}> = ({ icon, label, value, hint, testId }) => (
  <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 p-3">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">
      {icon}
      <span className="truncate">{label}</span>
    </div>
    <div
      className="font-display font-bold text-2xl mt-1 text-slate-900 dark:text-white tabular-nums"
      data-testid={testId}
    >
      {value}
    </div>
    {hint && (
      <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
        {hint}
      </div>
    )}
  </div>
);

export default AICartAnalyticsCard;
